import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Mail, Copy, RefreshCw, Trash2, Crown, UserCheck, Loader2, AlertCircle, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PERMISSION_LABELS = {
  products_write: { label: "Gestire prodotti", help: "Creare, modificare ed eliminare prodotti" },
  sales_write: { label: "Registrare vendite", help: "Creare e annullare vendite" },
  suppliers_write: { label: "Gestire fornitori", help: "CRUD anagrafica fornitori" },
  scan_receipts: { label: "Scansionare scontrini/DDT", help: "Usare l'AI per import veloce" },
  analytics_view: { label: "Vedere analisi", help: "Accesso a dashboard e analytics" },
  export_data: { label: "Esportare dati", help: "Scaricare CSV/Excel" },
  settings_manage: { label: "Modificare impostazioni", help: "Notifiche, email" },
  team_manage: { label: "Gestire team", help: "Invitare, rimuovere, modificare permessi" },
};

const PERMISSION_ORDER = [
  "products_write", "sales_write", "scan_receipts", "suppliers_write",
  "analytics_view", "export_data", "settings_manage", "team_manage",
];

export default function Team() {
  const navigate = useNavigate();
  const { team, refreshTeam } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [storeName, setStoreName] = useState("");
  const [renaming, setRenaming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const m = await api.teamMembers();
      setData(m);
    } catch (e) {
      toast.error("Errore caricamento team", { description: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (team?.store?.name) setStoreName(team.store.name);
  }, [team?.store?.name]);

  // Block access if not owner (defensive — the menu already hides this page)
  const isOwner = team?.role === "owner";

  if (!isOwner) {
    return (
      <div data-testid="team-no-access" className="mag-card p-12 text-center max-w-lg mx-auto">
        <AlertCircle size={32} className="mx-auto text-[#8E3B2A] mb-3" />
        <h2 className="text-lg font-semibold mb-2">Accesso riservato</h2>
        <p className="text-sm text-[#5C5F5A]">Solo l&apos;owner del negozio può gestire il team.</p>
        <Button onClick={() => navigate("/app/dashboard")} className="mt-4 bg-[#2E4A3B] hover:bg-[#21382C]">Torna alla dashboard</Button>
      </div>
    );
  }

  const code = team?.store?.invite_code;
  const codeExpires = team?.store?.invite_code_expires_at;

  const createInvite = async () => {
    setInviteBusy(true);
    try {
      const res = await api.teamCreateInvite({ email: inviteEmail || null, regenerate: !code });
      await refreshTeam();
      if (res.email_status === "sent") toast.success("Invito inviato per email", { description: `Codice: ${res.code}` });
      else if (res.email_status === "error") toast.warning("Codice generato, ma email non inviata", { description: `Condividilo manualmente: ${res.code}` });
      else toast.success("Codice invito pronto", { description: res.code });
      setInviteEmail("");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    } finally {
      setInviteBusy(false);
    }
  };

  const regenCode = async () => {
    setInviteBusy(true);
    try {
      await api.teamCreateInvite({ regenerate: true });
      await refreshTeam();
      toast.success("Nuovo codice generato");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    } finally {
      setInviteBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Codice copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const togglePerm = async (memberUserId, current, key, newValue) => {
    const next = { ...current, [key]: newValue };
    try {
      await api.teamUpdatePermissions(memberUserId, next);
      await load();
      toast.success("Permesso aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento", { description: e?.response?.data?.detail });
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      await api.teamRemoveMember(removeTarget.user_id);
      setRemoveTarget(null);
      await load();
      toast.success("Membro rimosso");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    }
  };

  const renameStore = async () => {
    if (!storeName.trim()) return;
    setRenaming(true);
    try {
      await api.teamUpdateStore({ name: storeName.trim() });
      await refreshTeam();
      toast.success("Nome negozio aggiornato");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div data-testid="team-page" className="space-y-8 max-w-5xl">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">Team</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
          Gestione negozio
        </h1>
        <p className="text-[#5C5F5A] mt-2">
          Invita cassieri, scegli cosa possono fare e gestisci il tuo team.
        </p>
      </div>

      {/* Store info */}
      <section className="mag-card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Pencil size={16} className="text-[#5C5F5A]" />
          <h2 className="text-base font-semibold">Nome negozio</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="store-name-input"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder="Es: La Bottega di Mario"
            className="bg-white border-[#EAE7E1]"
          />
          <Button
            data-testid="store-rename-btn"
            onClick={renameStore}
            disabled={renaming || !storeName.trim() || storeName === team?.store?.name}
            className="bg-[#2E4A3B] hover:bg-[#21382C]"
          >
            {renaming ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            Salva
          </Button>
        </div>
      </section>

      {/* Invite */}
      <section className="mag-card p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-[#2B5A3F]" />
          <h2 className="text-base font-semibold">Invita un cassiere</h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="invite-email-input"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@cassiere.it (opzionale)"
            className="bg-white border-[#EAE7E1] flex-1"
            type="email"
          />
          <Button
            data-testid="invite-create-btn"
            onClick={createInvite}
            disabled={inviteBusy}
            className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
          >
            {inviteBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Mail size={14} className="mr-2" />}
            {inviteEmail ? "Invia invito via email" : "Genera codice"}
          </Button>
        </div>

        {code && (
          <div className="bg-[#F4F1ED] border border-[#EAE7E1] rounded-lg p-4 space-y-2">
            <div className="text-xs uppercase tracking-wider text-[#8E918C]">Codice attivo</div>
            <div className="flex items-center gap-3 flex-wrap">
              <div
                data-testid="invite-code"
                className="font-mono text-2xl font-bold text-[#2E4A3B] tracking-[0.3em] bg-white border border-[#EAE7E1] rounded px-4 py-2"
              >
                {code}
              </div>
              <Button
                data-testid="invite-copy-btn"
                onClick={copyCode}
                variant="outline"
                size="sm"
                className="border-[#EAE7E1]"
              >
                <Copy size={14} className="mr-1.5" /> Copia
              </Button>
              <Button
                data-testid="invite-regen-btn"
                onClick={regenCode}
                variant="outline"
                size="sm"
                disabled={inviteBusy}
                className="border-[#EAE7E1] text-[#C65D47]"
              >
                <RefreshCw size={14} className="mr-1.5" /> Rigenera
              </Button>
            </div>
            <p className="text-xs text-[#8E918C]">
              {codeExpires ? `Scade il ${new Date(codeExpires).toLocaleDateString("it-IT")}` : "Codice senza scadenza"}.
              Il cassiere lo inserisce dopo il login Google in <span className="font-mono">/app/team/join</span>.
            </p>
          </div>
        )}
      </section>

      {/* Members table */}
      <section className="mag-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users size={16} className="text-[#5C5F5A]" />
          <h2 className="text-base font-semibold">Membri ({data?.members?.length ?? 0})</h2>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-[#8E918C]">
            <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Caricamento...
          </div>
        ) : (
          <div className="space-y-4">
            {(data?.members || []).map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                onTogglePerm={(key, val) => togglePerm(m.user_id, m.permissions, key, val)}
                onRemove={() => setRemoveTarget(m)}
              />
            ))}
          </div>
        )}
      </section>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere {removeTarget?.user?.name || "questo membro"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Perderà l&apos;accesso al negozio. I dati creati restano. Può essere reinvitato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="remove-member-confirm"
              onClick={removeMember}
              className="bg-[#C65D47] hover:bg-[#A64B38]"
            >
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemberCard({ member, onTogglePerm, onRemove }) {
  const isOwner = member.role === "owner";
  const initials = (member.user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div
      data-testid={`member-row-${member.user_id}`}
      className="border border-[#EAE7E1] rounded-lg p-4 bg-white space-y-3"
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          {member.user?.picture ? <AvatarImage src={member.user.picture} /> : null}
          <AvatarFallback className="bg-[#2E4A3B] text-white text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold truncate">{member.user?.name || "—"}</div>
            {isOwner ? (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#FDF6E3] border border-[#F2DEB0] text-[#8B6518] inline-flex items-center gap-1">
                <Crown size={10} /> Owner
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#F4F1ED] border border-[#EAE7E1] text-[#5C5F5A] inline-flex items-center gap-1">
                <UserCheck size={10} /> Cassiere
              </span>
            )}
            {member.is_me && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#E8F3EC] border border-[#C5E1D1] text-[#2B5A3F]">
                Tu
              </span>
            )}
          </div>
          <div className="text-xs text-[#8E918C] truncate">{member.user?.email || "—"}</div>
        </div>
        {!isOwner && !member.is_me && (
          <Button
            data-testid={`remove-member-${member.user_id}`}
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="text-[#C65D47] hover:bg-[#FCEAE8]"
            title="Rimuovi dal negozio"
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>

      {!isOwner && (
        <div className="border-t border-[#EAE7E1] pt-3">
          <div className="text-[11px] uppercase tracking-wider text-[#8E918C] mb-2">Permessi</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {PERMISSION_ORDER.map((key) => {
              const meta = PERMISSION_LABELS[key];
              const val = !!member.permissions?.[key];
              return (
                <div key={key} className="flex items-center justify-between gap-3 py-1">
                  <div className="min-w-0 flex-1">
                    <Label htmlFor={`perm-${member.user_id}-${key}`} className="text-sm font-medium cursor-pointer">
                      {meta.label}
                    </Label>
                    <div className="text-[11px] text-[#8E918C]">{meta.help}</div>
                  </div>
                  <Switch
                    id={`perm-${member.user_id}-${key}`}
                    data-testid={`perm-${member.user_id}-${key}`}
                    checked={val}
                    onCheckedChange={(v) => onTogglePerm(key, v)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
